// components/ChatWidget.jsx - Fix the infinite loop issue

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  TextField,
  Button,
  Avatar,
  Badge,
  CircularProgress,
  Fade,
  Slide,
  Chip
} from '@mui/material';
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Minimize as MinimizeIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  SupportAgent as SupportIcon,
  Check as CheckIcon,
  DoneAll as DoneAllIcon
} from '@mui/icons-material';
import { useChat } from '../context/ChatContext';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const ChatWidget = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const {
    socket,
    isConnected,
    chatOpen,
    minimized,
    currentChat,
    messages,
    unreadCount,
    isTyping,
    loading,
    userInfo,
    messagesEndRef,
    setUserInfo,
    startChat,
    sendMessage,
    sendTyping,
    markAsRead,
    closeChat,
    toggleChat,
    toggleMinimize,
    setChatOpen
  } = useChat();

  const [newMessage, setNewMessage] = useState('');
  const [showStartForm, setShowStartForm] = useState(!currentChat);
  const [formData, setFormData] = useState({
    name: userInfo?.name || user?.username || '',
    email: userInfo?.email || user?.email || '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update form data when userInfo changes, but prevent infinite loop
  useEffect(() => {
    if (userInfo) {
      setFormData(prev => ({
        ...prev,
        name: userInfo.name || prev.name || user?.username || '',
        email: userInfo.email || prev.email || user?.email || ''
      }));
    }
  }, [userInfo, user]);

  // Update showStartForm when currentChat changes
  useEffect(() => {
    if (currentChat) {
      setShowStartForm(false);
    } else {
      setShowStartForm(true);
    }
  }, [currentChat]);

  // Mark messages as read when chat is opened
  useEffect(() => {
    if (chatOpen && !minimized && currentChat) {
      markAsRead();
    }
  }, [chatOpen, minimized, currentChat, markAsRead]);

  const handleStartChat = useCallback(async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert(language === 'ar' ? 'الرجاء إدخال الاسم' : 'Please enter your name');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await startChat(formData.name, formData.email, formData.message);
    } catch (error) {
      console.error('Error starting chat:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, language, startChat]);

  const handleSendMessage = useCallback((e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    sendMessage(newMessage);
    setNewMessage('');
  }, [newMessage, sendMessage]);

  const handleTyping = useCallback((e) => {
    const value = e.target.value;
    setNewMessage(value);
    sendTyping(value.length > 0);
  }, [sendTyping]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Don't render if socket is not connected
  if (!socket) {
    return null;
  }

  return (
    <>
      {/* Chat Button */}
      <Fade in={!chatOpen}>
        <Box
          sx={{
            position: 'fixed',
            bottom: 90,
            right: 20,
            zIndex: 1300,
            cursor: 'pointer'
          }}
          onClick={toggleChat}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: '#FF3D00',
                color: '#FFFFFF',
                fontWeight: 'bold',
                right: 5,
                top: 5
              }
            }}
          >
            <Paper
              elevation={8}
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                backgroundColor: '#FFD700',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.1)',
                  boxShadow: '0 8px 25px rgba(255, 215, 0, 0.5)'
                }
              }}
            >
              <ChatIcon sx={{ color: '#000000', fontSize: 30 }} />
            </Paper>
          </Badge>
        </Box>
      </Fade>

      {/* Chat Window */}
      <Slide direction="up" in={chatOpen} mountOnEnter unmountOnExit>
        <Paper
          elevation={24}
          sx={{
            position: 'fixed',
            bottom: 90,
            right: 20,
            width: minimized ? 300 : 350,
            height: minimized ? 60 : 500,
            maxHeight: 'calc(100vh - 120px)',
            backgroundColor: '#0D0D0D',
            border: '1px solid #FFD700',
            borderRadius: '20px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1300,
            transition: 'all 0.3s ease',
            boxShadow: '0 10px 40px rgba(255, 215, 0, 0.2)'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 1.5,
              backgroundColor: '#FFD700',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer'
            }}
            onClick={minimized ? toggleMinimize : undefined}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: '#000000', width: 32, height: 32 }}>
                <SupportIcon sx={{ color: '#FFD700', fontSize: 20 }} />
              </Avatar>
              <Box>
                <Typography sx={{ color: '#000000', fontWeight: 800, fontSize: '1rem' }}>
                  {language === 'ar' ? 'الدعم الفني' : 'Customer Support'}
                </Typography>
                {!minimized && (
                  <Typography sx={{ color: '#000000', fontSize: '0.7rem', opacity: 0.8 }}>
                    {isConnected ? '🟢 Online' : '🔴 Offline'}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleMinimize();
                }}
                sx={{ color: '#000000' }}
              >
                {minimized ? <ExpandLessIcon /> : <MinimizeIcon />}
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleChat();
                }}
                sx={{ color: '#000000' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {!minimized && (
            <>
              {/* Start Chat Form */}
              {showStartForm && (
                <Box
                  component="form"
                  onSubmit={handleStartChat}
                  sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
                >
                  <Typography sx={{ color: '#FFD700', textAlign: 'center', mb: 1 }}>
                    {language === 'ar' ? 'كيف يمكننا مساعدتك؟' : 'How can we help you?'}
                  </Typography>
                  
                  <TextField
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'الاسم' : 'Name'}
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                    disabled={!!user || isSubmitting}
                    InputProps={{
                      sx: {
                        color: '#FFFFFF',
                        backgroundColor: '#1A1A1A',
                        '& fieldset': { borderColor: '#2D2D2D' },
                        '&:hover fieldset': { borderColor: '#FFD700' }
                      }
                    }}
                    InputLabelProps={{ sx: { color: '#B3B3B3' } }}
                  />
                  
                  <TextField
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    disabled={!!user || isSubmitting}
                    InputProps={{
                      sx: {
                        color: '#FFFFFF',
                        backgroundColor: '#1A1A1A',
                        '& fieldset': { borderColor: '#2D2D2D' },
                        '&:hover fieldset': { borderColor: '#FFD700' }
                      }
                    }}
                    InputLabelProps={{ sx: { color: '#B3B3B3' } }}
                  />
                  
                  <TextField
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'الرسالة (اختياري)' : 'Message (optional)'}
                    multiline
                    rows={3}
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    disabled={isSubmitting}
                    InputProps={{
                      sx: {
                        color: '#FFFFFF',
                        backgroundColor: '#1A1A1A',
                        '& fieldset': { borderColor: '#2D2D2D' },
                        '&:hover fieldset': { borderColor: '#FFD700' }
                      }
                    }}
                    InputLabelProps={{ sx: { color: '#B3B3B3' } }}
                  />
                  
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || !formData.name || !isConnected}
                    sx={{
                      backgroundColor: '#FFD700',
                      color: '#000000',
                      fontWeight: 700,
                      '&:hover': { backgroundColor: '#FFE55C' },
                      '&:disabled': { backgroundColor: '#2D2D2D', color: '#6B6B6B' }
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={24} sx={{ color: '#000000' }} />
                    ) : (
                      language === 'ar' ? 'بدء المحادثة' : 'Start Chat'
                    )}
                  </Button>
                </Box>
              )}

              {/* Messages Area */}
              {!showStartForm && currentChat && (
                <>
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: 'auto',
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      backgroundColor: '#0D0D0D'
                    }}
                  >
                    {messages.map((msg, index) => {
                      const isUser = msg.sender_type === 'user';
                      
                      return (
                        <Box
                          key={msg.id || index}
                          sx={{
                            display: 'flex',
                            justifyContent: isUser ? 'flex-end' : 'flex-start',
                            mb: 1
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: '80%',
                              backgroundColor: isUser ? '#FFD700' : '#1A1A1A',
                              color: isUser ? '#000000' : '#FFFFFF',
                              borderRadius: '12px',
                              p: 1.5,
                              borderBottomRightRadius: isUser ? '4px' : '12px',
                              borderBottomLeftRadius: isUser ? '12px' : '4px',
                              boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                            }}
                          >
                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                              {msg.message}
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 0.5,
                                mt: 0.5
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: isUser ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)',
                                  fontSize: '0.6rem'
                                }}
                              >
                                {formatTime(msg.created_at)}
                              </Typography>
                              {isUser && (
                                msg.is_read ? (
                                  <DoneAllIcon sx={{ fontSize: 12, color: 'rgba(0,0,0,0.6)' }} />
                                ) : (
                                  <CheckIcon sx={{ fontSize: 12, color: 'rgba(0,0,0,0.6)' }} />
                                )
                              )}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                    
                    {isTyping && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <Chip
                          label={language === 'ar' ? 'الدعم يكتب...' : 'Support is typing...'}
                          size="small"
                          sx={{
                            backgroundColor: '#1A1A1A',
                            color: '#B3B3B3',
                            animation: 'pulse 1.5s infinite'
                          }}
                        />
                      </Box>
                    )}
                    
                    <div ref={messagesEndRef} />
                  </Box>

                  {/* Message Input */}
                  <Box
                    component="form"
                    onSubmit={handleSendMessage}
                    sx={{
                      p: 1.5,
                      borderTop: '1px solid #2D2D2D',
                      display: 'flex',
                      gap: 1,
                      backgroundColor: '#0D0D0D'
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={language === 'ar' ? 'اكتب رسالتك...' : 'Type your message...'}
                      value={newMessage}
                      onChange={handleTyping}
                      InputProps={{
                        sx: {
                          color: '#FFFFFF',
                          backgroundColor: '#1A1A1A',
                          borderRadius: '20px',
                          '& fieldset': { borderColor: '#2D2D2D' },
                          '&:hover fieldset': { borderColor: '#FFD700' }
                        }
                      }}
                    />
                    <IconButton
                      type="submit"
                      disabled={!newMessage.trim()}
                      sx={{
                        backgroundColor: '#FFD700',
                        color: '#000000',
                        width: 40,
                        height: 40,
                        '&:hover': { backgroundColor: '#FFE55C' },
                        '&:disabled': { backgroundColor: '#2D2D2D', color: '#6B6B6B' }
                      }}
                    >
                      <SendIcon />
                    </IconButton>
                  </Box>
                </>
              )}
            </>
          )}
        </Paper>
      </Slide>

      {/* Styles for typing animation */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </>
  );
};

export default ChatWidget;